import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Star, ArrowRight, MapPin, Quote } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { api } from "../lib/api";
import { mediaSrc } from "../components/site/HeroSlides";

const rise = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } };

function Hero({ d }) {
  return (
    <section className="relative h-[100dvh] min-h-[640px] overflow-hidden bg-black text-white" data-testid="premium-hero">
      <motion.img src={mediaSrc(d.cover_image)} alt={`${d.couple_names} ${d.last_name}`} initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 6, ease: "easeOut" }} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
      <div className="relative h-full container-wide px-6 md:px-12 flex flex-col justify-end pb-20">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex items-center gap-2 text-[11px] uppercase tracking-[0.4em] text-[var(--gold)] mb-5"><Crown className="w-4 h-4" /> Locked In Forever Feature</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1 }} className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.95]" data-testid="premium-title">{d.headline}</motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="font-script text-4xl md:text-5xl text-[var(--gold)] mt-4" data-testid="premium-couple">{d.couple_names} {d.last_name}</motion.p>
        <p className="mt-6 flex items-center gap-2 text-sm text-white/70"><MapPin className="w-4 h-4" /> Proposed in Dubai · Married at {d.venue}, {d.city}</p>
      </div>
    </section>
  );
}

function Stats({ stats }) {
  return (
    <section className="bg-[#0d0d0d] text-white border-y border-[var(--gold)]/20" data-testid="premium-stats">
      <div className="container-wide px-6 md:px-12 py-14 grid grid-cols-2 md:grid-cols-6 gap-8">
        {stats.map((s, i) => (
          <motion.div key={s.label} {...rise} transition={{ ...rise.transition, delay: i * 0.08 }}>
            <p className="font-serif text-4xl md:text-5xl text-[var(--gold)]">{s.value}</p>
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/60 mt-2">{s.label}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Chapter({ c, i }) {
  const flip = i % 2 === 1;
  return (
    <motion.article {...rise} className={`grid md:grid-cols-12 gap-8 md:gap-14 items-center ${flip ? "md:[&>*:first-child]:order-2" : ""}`} data-testid={`premium-chapter-${i}`}>
      <div className="md:col-span-7 overflow-hidden rounded-sm group"><img src={mediaSrc(c.image)} alt={c.title} loading="lazy" className="w-full aspect-[3/2] object-cover transition-transform duration-[1.6s] group-hover:scale-105" /></div>
      <div className="md:col-span-5">
        <p className="text-[11px] uppercase tracking-[0.35em] text-[var(--gold)] mb-3">{String(i + 1).padStart(2, "0")} · {c.kicker}</p>
        <h2 className="font-serif font-light text-3xl md:text-4xl mb-5">{c.title}</h2>
        <p className="text-base leading-relaxed text-[var(--muted)]">{c.text}</p>
      </div>
    </motion.article>
  );
}

function Party({ party }) {
  return (
    <section className="section-pad bg-[#0d0d0d] text-white" data-testid="premium-party">
      <div className="container-wide px-6 md:px-12">
        <p className="font-script text-4xl text-[var(--gold)]">Our people</p>
        <h2 className="font-serif font-light text-4xl md:text-5xl mb-14">The wedding party</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-12">
          {party.map((p, i) => (
            <motion.div key={p.name} {...rise} transition={{ ...rise.transition, delay: (i % 5) * 0.07 }} className="group" data-testid={`premium-party-${i}`}>
              <div className="aspect-[4/5] overflow-hidden rounded-sm mb-4 ring-1 ring-[var(--gold)]/20"><img src={mediaSrc(p.photo_url)} alt={p.name} loading="lazy" className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700" /></div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--gold)]">{(p.wedding_party_role || "").replace(/_/g, " ")}</p>
              <p className="font-serif text-xl mt-1">{p.name}</p>
              {p.party_bio && <p className="text-xs text-white/55 mt-2 leading-relaxed">{p.party_bio}</p>}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews({ reviews }) {
  if (!reviews?.length) return null;
  return (
    <section className="section-pad" data-testid="premium-reviews">
      <div className="container-wide px-6 md:px-12 grid md:grid-cols-2 gap-8">
        {reviews.map((r) => (
          <motion.figure key={r.name} {...rise} className="border border-[var(--line)] p-8 md:p-10 bg-white">
            <Quote className="w-6 h-6 text-[var(--gold)] mb-4" />
            <blockquote className="font-serif text-xl md:text-2xl leading-snug">{r.text}</blockquote>
            <figcaption className="mt-6 flex items-center gap-3"><img src={mediaSrc(r.photo_url)} alt="" className="w-11 h-11 rounded-full object-cover" /><span><span className="block font-medium">{r.name}</span><span className="text-xs text-[var(--muted)]">{r.role}</span></span>
              <span className="ml-auto flex text-[var(--gold)]">{Array.from({ length: r.rating }).map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-current" />)}</span></figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}

export default function PremiumWeddingPage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/public/featured-wedding").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)]" data-testid="premium-error">This feature is being prepared. Check back soon.</div>;
  if (!d) return <div className="min-h-screen bg-black" />;
  return (
    <div className="bg-[var(--bg)]" data-testid="premium-wedding-page">
      <Seo title={`${d.headline} · ${d.couple_names} ${d.last_name}`} description="A Dubai superyacht proposal and a three-day palace wedding in Udaipur for 100 guests." />
      <Navbar overlay tone="light" />
      <Hero d={d} />
      <Stats stats={d.stats} />
      <section className="section-pad">
        <div className="container-wide px-6 md:px-12">
          <motion.p {...rise} className="max-w-3xl font-serif text-2xl md:text-3xl leading-snug mb-24" data-testid="premium-story">{d.story}</motion.p>
          <div className="space-y-24 md:space-y-32">{d.chapters.map((c, i) => <Chapter key={c.title} c={c} i={i} />)}</div>
        </div>
      </section>
      <Party party={d.party} />
      <Reviews reviews={d.reviews} />
      <section className="pb-24 text-center px-6" data-testid="premium-cta">
        <p className="font-script text-4xl text-[var(--gold)]">Your story is next</p>
        <h2 className="font-serif font-light text-4xl md:text-5xl mb-8">Plan a wedding people talk about forever</h2>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/signup" className="btn-gold inline-flex items-center gap-2" data-testid="premium-cta-start">Start planning <ArrowRight className="w-4 h-4" /></Link>
          <Link to={`/site/${d.slug}`} className="btn-outline-gold inline-flex" data-testid="premium-cta-site">Visit their wedding site</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
